import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
} from 'react';
import PropTypes from 'prop-types';
import Dropdown from './Dropdown';
import './SelectInput.scss';
import InputControl from '../InputControl/InputControl';
import Icon from '../Icon/Icon';
import useIsMounted from '../../utils/useIsMounted';

const getAriaLabel = (label, labelModifier) => {
  if (labelModifier === 'required') {
    return `* ${label}`;
  }

  if (labelModifier === 'optional') {
    return `${label} (Optional)`;
  }

  return label;
};

const SelectInput = ({
  id,
  classes: modifierClasses,
  name,
  label,
  options,
  labelModifier,
  disabled,
  error,
  microcopy,
  onSelect,
  hideLabel,
  condensed,
  attributes,
}) => {
  const inputRef = useRef();
  const selectRef = useRef();
  const dropdownRef = useRef();
  const buttonRef = useRef();

  // visible scroll setTimeout
  const scrollTimerRef = useRef();

  const [prevSelected, setPrevSelected] = useState();
  const [selected, setSelected] = useState(
    options.find((option) => option.selected)
  );

  const [showDropdown, setShowDropDown] = useState(false);

  const isMounted = useIsMounted();

  const handleToggle = (e) => {
    e.preventDefault();
    setShowDropDown(!showDropdown);
  };

  const handleSelect = (option, triggerToggle, key) => {
    setSelected(option);
    if (triggerToggle) {
      // Collapse menu by setting dropdown to false for tab and focusing on button for enter key. If key
      // is undefined, then we assume we're dealing with a click handler and collapse the menu
      if (key === 9 || key === 27 || key === undefined) {
        setShowDropDown(false);
      }
      buttonRef.current.focus();
    }
  };

  const handleKeyDown = (event) => {
    const isDropdownVisible = showDropdown;
    if (!disabled) {
      if (showDropdown && event.keyCode !== 13) {
        dropdownRef.current.handleParentKeyDown(
          event,
          isDropdownVisible
        );
      } else if (
        !showDropdown &&
        (event.keyCode === 38 ||
          event.keyCode === 40 ||
          event.keyCode === 13 ||
          event.keyCode === 32)
      ) {
        // Using keys and menu isn't showing
        selectRef.current.querySelector(
          '.dropdown.option'
        ).style.display = 'flex';
        setShowDropDown(!showDropdown);
        dropdownRef.current.handleParentKeyDown(
          event,
          isDropdownVisible
        );
      } else if (showDropdown && event.keyCode === 13) {
        // Using enter key when dropdown shows, collapse dropdown and focus on button
        setShowDropDown(!showDropdown);
        buttonRef.current.focus();
      }
    }
  };
  const handleDomClick = useCallback(
    (e) => {
      if (
        e.target === selectRef.current ||
        selectRef.current.contains(e.target)
      ) {
        return false;
      }

      if (showDropdown) {
        setShowDropDown(false);
      }
      return true;
    },
    [showDropdown, selected]
  );

  const setValue = () => {
    let defaultOption;
    if (options.length) {
      defaultOption = options.find(
        (eachValue) => eachValue.selected === true
      );
    }
    if (!defaultOption) {
      defaultOption = { name: '', value: undefined };
    }

    setSelected(defaultOption);
    setPrevSelected(defaultOption);
  };

  useEffect(() => {
    if (showDropdown) {
      document.addEventListener('click', handleDomClick, false);
    } else {
      document.removeEventListener('click', handleDomClick, false);
    }
    return () => {
      document.removeEventListener('click', handleDomClick, false);
    };
  }, [showDropdown]);

  useEffect(() => {
    if (isMounted) {
      const isValueUpdated =
        !prevSelected || selected.value !== prevSelected.value;

      const isValuePopulated = selected.value !== undefined;

      if (!showDropdown && isValueUpdated && isValuePopulated) {
        onSelect((selected && selected.value) || '');
        setPrevSelected(selected);
      }
    }
  }, [selected, showDropdown]);

  /** visible scroll when the options are many */
  useEffect(() => {
    clearTimeout(scrollTimerRef.current);
    const selector = '.dropdown.option';

    const showList = () => {
      const listElem = selectRef.current.querySelector(selector);
      if (listElem) listElem.style.display = 'flex';
    };

    const hideList = () => {
      const listElem = selectRef.current.querySelector(selector);
      if (listElem) listElem.style.display = 'none';
    };

    const showScroll = () => {
      const listElem = selectRef.current.querySelector(selector);
      if (listElem) listElem.style.overflow = 'auto';
    };

    const hideScroll = () => {
      const listElem = selectRef.current.querySelector(selector);
      if (listElem) listElem.style.overflow = 'hidden';
    };

    if (showDropdown) {
      showList();
      scrollTimerRef.current = setTimeout(showScroll, 200);
    } else {
      hideScroll();
      scrollTimerRef.current = setTimeout(hideList, 200);
    }

    // prevent callback from running after unmount
    return () => {
      clearTimeout(scrollTimerRef.current);
    };
  }, [showDropdown, selectRef]);

  useEffect(setValue, [options]);

  const optionStateClasses = [
    'axiom-selected-option selected-option',
  ];
  if (disabled) {
    optionStateClasses.push('disabled');
  }
  if (condensed) {
    optionStateClasses.push('condensed');
  }
  if (showDropdown) {
    optionStateClasses.push('up');
  } else {
    optionStateClasses.push('down');
  }

  const maxLength = options.reduce((a, b) => {
    if (a.name && b.name) {
      return a.name.length > b.name.length ? a : b;
    }
    return 10;
  });

  const widthSelect = maxLength.name.length * 8 + (42 + 8 * 4);

  const normalizedPropClasses =
    modifierClasses instanceof Array
      ? modifierClasses
      : [modifierClasses];

  const controllerClasses = [
    'axiom-select-input',
    ...normalizedPropClasses,
  ];

  return (
    <InputControl
      id={id}
      classes={controllerClasses}
      label={label}
      labelModifier={labelModifier}
      error={error}
      microcopy={microcopy}
      hideLabel={hideLabel}
    >
      <div
        className='input-control--select--wrap'
        data-testid='select-wrapper'
        ref={selectRef}
        style={{ width: `${widthSelect}px` }}
      >
        <div
          data-testid='selected-label'
          onKeyUp={handleKeyDown}
          className={optionStateClasses.join(' ')}
          onClick={handleToggle}
          aria-describedby={
            error || microcopy ? `${id}_messages` : null
          }
          aria-haspopup='listbox'
          aria-owns={`${id}_textbox`}
          aria-label={getAriaLabel(label, labelModifier)}
          aria-controls={`${id}_option-list`}
          aria-disabled={disabled ? true : undefined}
          aria-invalid={error ? true : undefined}
          aria-required={
            labelModifier === 'required' ? true : undefined
          }
          ref={buttonRef}
          id={id}
          role='combobox'
          aria-expanded={showDropdown ? 'true' : 'false'}
          tabIndex={0}
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...attributes}
        >
          {selected && selected.name}
          <Icon name={showDropdown ? 'caret-up' : 'caret-down'} />
        </div>

        <input
          ref={inputRef}
          id={`${id}_textbox`}
          name={name}
          type='hidden'
          className='axiom-select-input--input'
          defaultValue={selected ? selected.value : undefined}
        />

        <Dropdown
          ref={dropdownRef}
          options={options}
          selected={selected}
          condensed={condensed}
          handleSelect={handleSelect}
          isVisible={showDropdown}
          labelId={id}
        />
      </div>
    </InputControl>
  );
};

SelectInput.propTypes = {
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      value: PropTypes.string,
      selected: PropTypes.bool,
      disabled: PropTypes.bool,
    })
  ).isRequired,
  labelModifier: PropTypes.oneOf(['required', 'optional']),
  disabled: PropTypes.bool,
  error: PropTypes.string,
  microcopy: PropTypes.string,
  onSelect: PropTypes.func.isRequired,
  hideLabel: PropTypes.bool,
  condensed: PropTypes.bool,
  attributes: PropTypes.objectOf(PropTypes.any),
  classes: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.string),
  ]),
};

SelectInput.defaultProps = {
  labelModifier: undefined,
  disabled: false,
  error: undefined,
  microcopy: undefined,
  hideLabel: false,
  condensed: false,
  attributes: {},
  classes: [],
};

export default SelectInput;
